import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { AuthResponse, LoginRequest, RegisterRequest, UserRole } from '../types/auth';
import { API_BASE_URL } from '../utils/config';
import { reportAxiosUnauthorized, tokenFromAuthorizationHeader } from '../utils/session';
import { serverErrorMessage, TOO_MANY_REQUESTS_MESSAGE } from '../utils/apiErrors';
import {
  afterUnauthorized,
  bumpAuthGeneration,
  REFRESH_UNAVAILABLE_MESSAGE,
  RefreshAttempt,
  refreshFrom,
  resolveRequestToken,
  tokenExpiryMs,
} from '../utils/tokenRefresh';

// [Audit napló – kliens forrás, 2026-09-12] Minden webes kérésen jelezzük a szervernek, hogy
// webes admin klienstől jött (a backend ez alapján tölti az AuditLog "clientSource" mezőjét,
// mobil kliensnél "mobile/<verzió>" érkezik). Szándékosan a globális `axios.defaults`-on, MÉG
// az `axios.create()` HÍVÁS ELŐTT állítjuk be: (a) így a lenti közös `api` példány saját
// defaults.headers.common-jába is bekerül a létrehozáskori merge miatt, (b) és mivel a legtöbb
// service saját, közvetlen axios.get/post hívást használ (nem ezt az `api` példányt), azok is
// megkapják, hiszen mindannyian ugyanazt a globális axios szingletont importálják.
axios.defaults.headers.common['X-Client'] = 'web';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * [Web 7 / T1, 2026-10-05] CSENDES BEJELENTKEZÉS-MEGÚJÍTÁS az axios-utakon (az `api` példány ÉS a
 * globális `axios`). A döntések és a verseny-védelem indoklása: `utils/tokenRefresh.ts` fejléce.
 *
 * Jelzők a kérés-konfiguráción (az axios az ismeretlen kulcsokat az ismétlésnél is átviszi):
 *   - `_pinyoRefreshAttempt`: a kérés INDULÁSAKORI megújítási kísérlet sorsa (`RefreshAttempt`) — a 401-ág
 *     (`afterUnauthorized`) ebből tudja, hogy elfogyott-e a kérés EGY megújítása (F-B), és hogy egy
 *     átmeneti bukás után a 401 nem lejárat (F-A). [Web 7 / T1 javító kör 2, 2026-10-06] KORÁBBAN
 *     `_pinyoRefreshTried` (igen/nem): az átmeneti és a többi kimenetet nem különböztette meg;
 *   - `_pinyoRetried`: ez már a 401 utáni ismétlés → a 401-e a lejárat-jelzőre megy, nincs harmadik kör.
 */
type PinyoRequestConfig = InternalAxiosRequestConfig & { _pinyoRefreshAttempt?: RefreshAttempt; _pinyoRetried?: boolean };

function readAuthorization(headers: any): unknown {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const v = headers.get('Authorization');
    if (v !== undefined && v !== null) return v;
  }
  return headers.Authorization ?? headers.authorization;
}

function writeAuthorization(config: InternalAxiosRequestConfig, token: string): void {
  const headers: any = config.headers;
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = { ...(headers ?? {}), Authorization: `Bearer ${token}` } as any;
  }
}

/** Kérés indulásakor: közel lejárt / lejárt tokennél előbb csendes megújítás, majd az ÚJ tokennel megy. */
async function applyFreshToken(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const sent = tokenFromAuthorizationHeader(readAuthorization(config.headers));
  if (!sent) return config;
  try {
    const { token, attempt } = await resolveRequestToken(sent, config.url);
    if (token !== sent) writeAuthorization(config, token);
    // Az ismétlésnél (a 401-ág `client.request(config)`-ja) ez újra lefut — egy `none` ott nem írhatja
    // felül az eredeti kísérlet sorsát.
    if (attempt !== 'none') (config as PinyoRequestConfig)._pinyoRefreshAttempt = attempt;
  } catch (err: any) {
    // A megújítás alatt kijelentkezés / felhasználóváltás történt: a kérés NEM mehet ki (más nevében sem).
    if (err?.name === 'AuthSessionChangedError') throw new axios.CanceledError(err.message);
    throw err;
  }
  return config;
}

/**
 * [Web 7 / T1 javító kör 2, 2026-10-06] F-A: a 401-es axios-hibát ÁTMENETI elérhetetlenséggé alakítja. A
 * meglévő hibakezelők a `response.data.message`-et olvassák (`apiErrorMessage` → `serverErrorMessage`, és
 * a régi `err.response?.data?.message` minta is) — így a felület a `REFRESH_UNAVAILABLE_MESSAGE`-et írja
 * ki, változtatás nélkül. A státusz 503 lesz: a 401-specifikus ágak (lejárat-szövegek,
 * `CoachPaymentManagement` „lejárat miatt bukott" jelzője) NEM futnak rá. Az eredeti státusz
 * `pinyoOriginalStatus`-ként megmarad (hibakereséshez).
 */
function toRefreshUnavailableError(error: any): any {
  const original = error.response ?? {};
  error.response = {
    ...original,
    status: 503,
    statusText: 'Service Unavailable',
    data: { message: REFRESH_UNAVAILABLE_MESSAGE },
  };
  error.message = REFRESH_UNAVAILABLE_MESSAGE;
  error.pinyoRefreshUnavailable = true;
  error.pinyoOriginalStatus = original.status;
  return error;
}

type UnauthorizedHandling = { kind: 'retried'; result: Promise<any> } | { kind: 'unavailable'; error: any } | null;

/**
 * 401 után: EGY megújítási kísérlet és a kérés megismétlése (ha a kérés indulásakor még nem volt
 * kísérlet). Ha nincs új token, a meglévő lejárat-jelzés (`reportAxiosUnauthorized`) jön — változatlanul.
 * [Web 7 / T1 javító kör 2, 2026-10-06] F-A: ha a megújítás ÁTMENETI hibával bukott → `unavailable`
 * (nincs lejárat-jelzés, a tároló marad). F-C: CSAK 401-re — a 403 / 5xx változatlanul a hívóhoz megy.
 * @returns `retried`: az ismételt kérés; `unavailable`: az átalakított hiba; `null`: a meglévő út.
 */
async function handleUnauthorized(error: any, client: AxiosInstance): Promise<UnauthorizedHandling> {
  const config = error?.config as PinyoRequestConfig | undefined;
  if (error?.response?.status !== 401 || !config) return null;
  if (config._pinyoRetried) return null;
  const sent = tokenFromAuthorizationHeader(readAuthorization(config.headers));
  // F-B: az indításkori kísérlet sorsa — kérésenként legfeljebb EGY megújítás.
  const next = await afterUnauthorized(sent, config.url, config._pinyoRefreshAttempt ?? 'none');
  if (next.kind === 'unavailable') return { kind: 'unavailable', error: toRefreshUnavailableError(error) };
  if (next.kind !== 'retry') return null;
  config._pinyoRetried = true;
  writeAuthorization(config, next.token);
  return { kind: 'retried', result: client.request(config) };
}

// Add token to requests if available
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');
  const isAuthEndpoint = config.url?.includes('/auth/');
  const isPublicEndpoint = config.url?.includes('/auth/login') || config.url?.includes('/auth/register');

  // [validátor F-A, 2026-10-03] A kifejezetten megadott fejlécet (a `logout()` a KILÉPÉSKOR
  // érvényes tokent adja át) nem írjuk felül a pillanatnyi tárolt tokennel.
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (isAuthEndpoint && !isPublicEndpoint) {
    // Only warn for protected auth endpoints in development, not for login/register
    if (process.env.NODE_ENV === 'development') {
      console.warn('AuthService: No token available for protected endpoint:', config.url);
    }
  }
  // [Web 7 / T1, 2026-10-05] közel lejárt / lejárt tokennél előbb csendes megújítás.
  return applyFreshToken(config);
});

// Handle response errors and token expiration
//
// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A 401-ág KORÁBBAN (a régi
// `authService.ts:42-56`) csendben törölte a `token`/`user` bejegyzést, és csak akkor ugrott a
// `/`-ra, ha a cím nem `/login` és nem `/` volt — a Dashboard a `/`-on fut, így a felület
// „bejelentkezve" maradt, miközben minden további kérés hibát adott (jelentés 4.5). Most a közös
// jelzőt hívjuk (`utils/session.ts`); a kijelentkeztetést / újra-belépő ablakot az `AuthContext`
// végzi, a tárolt adatot is ő törli. A kivételeket (login/logout/refresh, token nélküli kérés,
// elavult token) a `reportAxiosUnauthorized` dönti el — a KÜLDÖTT fejléc alapján.
//
// [Web 7 / T1, 2026-10-05] A 401 ELŐTT egy csendes megújítás + a kérés megismétlése
// (`handleUnauthorized`); a lejárat-jelzés csak akkor jön, ha ez nem sikerült.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const handled = await handleUnauthorized(error, api);
    if (handled?.kind === 'retried') return handled.result;
    if (handled?.kind === 'unavailable') return Promise.reject(handled.error); // F-A: nincs lejárat-jelzés

    reportAxiosUnauthorized(error);

    if (error.response?.status === 429) {
      // [Web UX 3. csomag (D), 2026-10-04] a szerver indoklása a közös olvasón át (mindhárom törzs-alak); a 401-ághoz
      // (Login saját szövegei) ez nem nyúl, csak a 429-hez.
      // [Web UX 9 / T2, 2026-10-13, validátor F1] A testetlen 429 tartaléka a közös mondat (`apiErrors.ts`
      // TOO_MANY_REQUESTS_MESSAGE), hogy a kliensben ne legyen két külön 429-szöveg.
      const message = serverErrorMessage(error.response?.data) || TOO_MANY_REQUESTS_MESSAGE;
      error.message = message;
    }

    return Promise.reject(error);
  }
);

// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A GLOBÁLIS axios 401-e. Az `api` példány
// (`axios.create()`) interceptora a globális `axios`-ra NEM hat, és fordítva: a globálisan
// regisztrált interceptor sem fut az `api` példányon (mindkét irányban mérve, lásd
// `services/__tests__/authService.sessionExpired.test.ts`) — ezért kell mindkettő, és ezért nincs
// dupla jelzés sem (egy kérés csak az egyiken megy át; a zár ettől függetlenül is véd).
// A globálist használja: attendanceService, auditLogService, coachAvailabilityService,
// coachPaymentService, eventService, plannedEventService, trainingBalanceService és az
// AdminMessages komponens (leltár 2(a)). Ez a modul az indulásnál BIZTOSAN betöltődik
// (`App.tsx` → `AuthContext` → `authService`), ugyanúgy, mint a fenti `X-Client` alapértelmezés.
//
// IDEMPOTENS REGISZTRÁCIÓ [validátor M3, 2026-10-03]: a globális `axios` szingleton a modul
// újra-kiértékelését túléli (fejlesztői HMR, tesztben `jest.isolateModules`), ezért egy sima
// `use()` minden újratöltésnél EGY ÚJABB interceptort fűzne a láncra. Az előző azonosítóját a
// `window`-on tartjuk, és újra-kiértékeléskor előbb kivesszük (`eject`) — így mindig pontosan egy él.
// (Az `api` példányt a modul minden kiértékeléskor újra létrehozza, ott ez nem kell.)
const GLOBAL_INTERCEPTOR_KEY = '__pinyoGlobalAuthInterceptorId';
const interceptorHost = window as unknown as Record<string, number | undefined>;
const previousInterceptorId = interceptorHost[GLOBAL_INTERCEPTOR_KEY];
if (typeof previousInterceptorId === 'number' && typeof axios.interceptors.response.eject === 'function') {
  axios.interceptors.response.eject(previousInterceptorId);
}
interceptorHost[GLOBAL_INTERCEPTOR_KEY] = axios.interceptors.response.use(
  (response) => response,
  // [Web 7 / T1, 2026-10-05] mint az `api` példánynál: előbb egy megújítás + ismétlés, csak utána jelzés.
  async (error) => {
    const handled = await handleUnauthorized(error, axios);
    if (handled?.kind === 'retried') return handled.result;
    if (handled?.kind === 'unavailable') return Promise.reject(handled.error); // F-A: nincs lejárat-jelzés
    reportAxiosUnauthorized(error);
    return Promise.reject(error);
  }
);

// [Web 7 / T1, 2026-10-05] A GLOBÁLIS axios REQUEST-interceptora: a szolgáltatások a fejlécet maguk
// számolják (`getAuthHeader()` → `Bearer <tárolt token>`); ha az a token közel lejárt / lejárt, itt
// cseréljük az újra (csak ha a fejléc tokenje a TÁROLT token — egy kifejezetten más token, pl. a
// kilépésé, érintetlen marad). Ugyanaz az idempotens regisztráció, mint a response-ágon.
const GLOBAL_REQUEST_INTERCEPTOR_KEY = '__pinyoGlobalAuthRequestInterceptorId';
const previousRequestInterceptorId = interceptorHost[GLOBAL_REQUEST_INTERCEPTOR_KEY];
if (typeof previousRequestInterceptorId === 'number' && typeof axios.interceptors.request.eject === 'function') {
  axios.interceptors.request.eject(previousRequestInterceptorId);
}
interceptorHost[GLOBAL_REQUEST_INTERCEPTOR_KEY] = axios.interceptors.request.use((config) => applyFreshToken(config));

/** Tároló ürítése, de csak ha még a `sentToken` van benne (közben nem lépett be senki újra). */
function clearStoredAuthIfUnchanged(sentToken: string | null): void {
  if (localStorage.getItem('token') !== sentToken) return;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  async register(userData: RegisterRequest): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/register', userData);
    return response.data;
  },

  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/verify-email', { token });
    return response.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(data: { token: string; newPassword: string; confirmNewPassword: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/reset-password-confirmed', data);
    return response.data;
  },

  async getUserRoles(): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>('/auth/user-roles');
    return response.data;
  },

  /**
   * [Web UX 2. csomag, 2. javító kör, validátor F-A, 2026-10-03] A helyi tárolót SZINKRON, a hívás
   * ELEJÉN ürítjük. KORÁBBAN (a régi `authService.ts:130-133`) a `finally`-ág a `POST /auth/logout`
   * VÁLASZA UTÁN törölt: lassú kilépő kérés + gyors újra-belépés esetén addigra már az ÚJ token volt
   * a tárolóban, és azt törölte — a `user` állapot maradt, a token nem, a token nélküli kérések
   * 401-e pedig (helyesen) nem jelez (`utils/session.ts` `reportUnauthorized`, `!sentToken`) →
   * „élőhalott" felület (mérés: a validátor `p8-slowlogout` próbája, E2E: `flow-session-expired`
   * 6. teszt). A kilépő kérés a KILÉPÉSKOR érvényes tokent viszi, kifejezett fejléccel (a request-
   * interceptor a kifejezett fejlécet nem írja felül).
   */
  async logout(): Promise<void> {
    // [Web 7 / T1, 2026-10-05] Egy épp futó csendes megújítás válasza a kilépés után NEM írhatja vissza
    // a tokent (a mobil M1 lelete) — a generáció léptetése ezt zárja (`utils/tokenRefresh.ts`).
    bumpAuthGeneration();
    const sentToken = localStorage.getItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    try {
      await api.post('/auth/logout', undefined, sentToken ? { headers: { Authorization: `Bearer ${sentToken}` } } : undefined);
    } catch (error) {
      // Only log logout errors in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Logout request failed:', error);
      }
    }
  },

  // [validátor F-A, 2026-10-03] A `validateToken` hiba-ága is a válasz UTÁN törölt; ma nincs
  // hívója, de ha lesz, ugyanaz a késői-törlés csapda ne élhessen: csak akkor ürítünk, ha a
  // tárolóban MÉG a kéréskor küldött token van.
  // [Web 7 / T1 javító kör 1, 2026-10-06] M4: SZÁNDÉKOSAN megtartva (hívó nélkül). Figyelem, ha valaki bekötné: a hiba-ága
  // MINDEN hibára (átmeneti 5xx / hálózati hibára is) töröl — ez ellentétes a csendes megújítás
  // F2-szabályával (átmeneti hiba nem léptet ki); bekötés előtt ezt az ágat a 401-re kell szűkíteni.
  async validateToken(): Promise<boolean> {
    const sentToken = localStorage.getItem('token');
    try {
      const response = await api.get('/auth/validate');
      return response.status === 200;
    } catch (error) {
      clearStoredAuthIfUnchanged(sentToken);
      return false;
    }
  },

  /**
   * [Web 7 / T1, 2026-10-05] A kézi megújítás is a KÖZÖS magon megy (`utils/tokenRefresh.ts`
   * `refreshFrom`: egy futás, generáció-őr, tároló-írás). KORÁBBAN saját `POST /auth/refresh`-t küldött,
   * és hiba esetén törölte a tárolót — hívója nem volt. Ma a hiba NEM töröl: a lejárat-jelzés a
   * meglévő 401-úton jön (ugyanaz a viselkedés, mint a csendes megújításnál).
   *
   * [Web 7 / T1 javító kör 1, 2026-10-06] M4: KORÁBBAN `expiration: ''`-t adott vissza; ma a szerver valós lejárata
   * (`AuthResponseDto.Expiration`) megy tovább — ha a válasz nem tartalmazta, üres szöveg (a típus kötelező).
   * Ma sincs hívója (a csendes megújítás a közös magon, `utils/tokenRefresh.ts`, fut).
   */
  async refreshToken(): Promise<AuthResponse | null> {
    const sentToken = localStorage.getItem('token');
    if (!sentToken) return null;
    const outcome = await refreshFrom(sentToken);
    return outcome.kind === 'ok'
      ? { token: outcome.token, expiration: outcome.expiration ?? '', user: outcome.user }
      : null;
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      // Sérült/érvénytelen 'user' bejegyzés (pl. csonka JSON vagy az "undefined"
      // literál) — töröljük, hogy az app ne fagyjon be induláskor a JSON.parse hibától.
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    }
  },

  /**
   * [Web 7 / T1 javító kör 2, 2026-10-06] M3: KORÁBBAN saját dekóder (`atob(token.split('.')[1])`) a
   * base64url → base64 átalakítás NÉLKÜL: ha a payload `-`/`_` karaktert tartalmazott (a JWT base64url!),
   * az `atob` hibát dobott → érvényes tokennél is `false` → az `AuthContext` init minden újratöltéskor
   * fölösleges megújítást indított. Ma a közös dekóder (`utils/tokenRefresh.ts` `tokenExpiryMs`) dönt —
   * egy dekóder van. Teszt: `tokenRefresh.test.ts` „M3 (isAuthenticated)".
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const exp = tokenExpiryMs(token);
    return exp !== null && exp > Date.now();
  },
};

export default api;
